import { useState } from 'react';

import { addYears, isAfter, isValid, subYears } from 'date-fns';
import { useTranslation } from 'react-i18next';

import EventIcon from '@mui/icons-material/Event';
import { Box, IconButton, TextField } from '@mui/material';
import type { DateRange } from '@mui/x-date-pickers-pro';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { WeekNumberPicker } from 'components/shared';

type Props = {
  isSetToMonth: boolean;
  dateRange: DateRange<Date>;
  onChangeStartDate: (d: Date | null) => void;
  onChangeEndDate: (d: Date | null) => void;
};

export default function MonthOrWeekRange({
  isSetToMonth,
  dateRange,
  onChangeStartDate,
  onChangeEndDate
}: Props) {
  const { t } = useTranslation();
  const [monthStartOpen, setMonthStartOpen] = useState(false);
  const [monthEndOpen, setMonthEndOpen] = useState(false);

  const handleMonthStartChange = (d: Date | null) => {
    setMonthStartOpen(false);
    onChangeStartDate(d);
  };

  const handleMonthEndChange = (d: Date | null) => {
    setMonthEndOpen(false);
    onChangeEndDate(d);
  };

  const dateRangeMismatchError = Boolean(
    dateRange[0] && dateRange[1] && isAfter(dateRange[0], dateRange[1])
  );

  return (
    <Box position="relative">
      {isSetToMonth && (
        <Box display="flex" alignItems="end">
          <DatePicker
            views={['month', 'year']}
            label={t('startDate')}
            value={dateRange[0]}
            open={monthStartOpen}
            onClose={() => setMonthStartOpen(false)}
            onChange={onChangeStartDate}
            onMonthChange={handleMonthStartChange}
            onYearChange={handleMonthStartChange}
            minDate={subYears(new Date(), 10)}
            maxDate={addYears(new Date(), 10)}
            renderInput={(params) => (
              <TextField
                {...params}
                name={t('startDate')}
                helperText={
                  dateRange[0] &&
                  !isValid(dateRange[0]) &&
                  t('adminPortal.realization.errors.invalidDate')
                }
                error={
                  (dateRange[0] && !isValid(dateRange[0])) ||
                  dateRangeMismatchError
                }
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setMonthStartOpen((prev) => !prev)}
                      size="small"
                    >
                      <EventIcon />
                    </IconButton>
                  )
                }}
              />
            )}
          />
          <Box mx={2}>{t('until')}</Box>
          <DatePicker
            views={['month', 'year']}
            label={t('endDate')}
            value={dateRange[1]}
            open={monthEndOpen}
            onClose={() => setMonthEndOpen(false)}
            onChange={onChangeEndDate}
            onMonthChange={handleMonthEndChange}
            onYearChange={handleMonthEndChange}
            minDate={subYears(new Date(), 10)}
            maxDate={addYears(new Date(), 10)}
            renderInput={(params) => (
              <TextField
                {...params}
                name={t('endDate')}
                helperText={
                  dateRange[1] &&
                  !isValid(dateRange[1]) &&
                  t('adminPortal.realization.errors.invalidDate')
                }
                error={
                  (dateRange[1] && !isValid(dateRange[1])) ||
                  dateRangeMismatchError
                }
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setMonthEndOpen((prev) => !prev)}
                      size="small"
                    >
                      <EventIcon />
                    </IconButton>
                  )
                }}
              />
            )}
          />
        </Box>
      )}
      {!isSetToMonth && (
        <Box display="flex" alignItems="end">
          <WeekNumberPicker
            placeholder={t('startDate')}
            date={dateRange[0]}
            onChange={onChangeStartDate}
            error={dateRangeMismatchError}
          />
          <Box mx={2}>{t('until')}</Box>
          <WeekNumberPicker
            placeholder={t('endDate')}
            date={dateRange[1]}
            onChange={onChangeEndDate}
            error={dateRangeMismatchError}
          />
        </Box>
      )}
      {dateRangeMismatchError && (
        <Box position="absolute" left={0} bottom={-24} sx={{ color: 'red' }}>
          {t('dateRangeMismatch', {
            startDate: t('startDate'),
            endDate: t('endDate')
          })}
        </Box>
      )}
    </Box>
  );
}
